import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import FormField from '../../../../components/common/FormField';
import { CompletionData } from '../../../../models/CompletionModels';
import { useFormValidation, validateTabFields } from '../../../../utilities/completionsUtils';

interface CompletionsCompletionTabProps {
  completion: CompletionData;
  setCompletion: React.Dispatch<React.SetStateAction<CompletionData>>;
  externalValidationErrors?: { [fieldName: string]: string };
  updateValidationError: (tabname: string, errors: { [fieldName: string]: string }) => void;
}

const CompletionsCompletionTab = forwardRef<any, CompletionsCompletionTabProps>(
  ({ completion, setCompletion, externalValidationErrors = {}, updateValidationError }, ref) => {
    const [completionsInternalValues, setCompletionsInternalValues] = useState<CompletionData>(
      JSON.parse(JSON.stringify(completion)),
    );

    useEffect(() => {
      setCompletion(JSON.parse(JSON.stringify(completionsInternalValues)));
    }, [completionsInternalValues, setCompletion]);

    const validation = useFormValidation({
      tabName: 'completion',
    });

    const handleFieldChange = useCallback(
      (fieldName: string, value: any) => {
        setCompletionsInternalValues((prev) => {
          const updatedData = { ...prev, [fieldName]: value };
          // Validate with the complete updated data
          const fieldErrors = validateTabFields('completion', updatedData);
          validation.setErrors?.(fieldErrors);
          updateValidationError('completion', fieldErrors);
          return updatedData;
        });
      },
      [validation],
    );

    const displayErrors =
      Object.keys(externalValidationErrors).length > 0 ? externalValidationErrors : validation.errors;

    useImperativeHandle(ref, () => ({
      triggerValidation: (data: CompletionData) => {
        validation.validateAllFields(data);
      },
    }));

    // Field configuration
    const fieldConfigs = {
      wellDepths: [
        { label: 'Pump MD (ft)', name: 'PumpMeasured', type: 'number', required: true },
        { label: 'Pump TVD (ft)', name: 'PumpTrueVertical', type: 'number', required: true },
        { label: 'Top of perfs MD (ft)', name: 'BottomholeMeasured', type: 'number', required: true },
        { label: 'Top of perfs TVD (ft)', name: 'BottomholeTrueVertical', type: 'number', required: true },
      ],
      wellGeometry: [
        { label: 'Tubing OD (in)', name: 'GeometryTubingOD', type: 'number', required: true },
        { label: 'Tubing ID (in)', name: 'GeometryTubingID', type: 'number', required: true },
        { label: 'Tubing Rgh (in)', name: 'GeometryTubingRoughness', type: 'number', required: true },
        { label: 'Casing OD (in)', name: 'GeometryCasingOD', type: 'number', required: true },
        { label: 'Casing ID (in)', name: 'GeometryCasingID', type: 'number', required: true },
        { label: 'Casing Rgh (in)', name: 'GeometryCasingRoughness', type: 'number', required: true },
      ],
      wellEquipment: [
        { label: 'Motor OD (in)', name: 'MotorOD', type: 'number' },
        { label: 'Motor power @60 Hz (hp)', name: 'MotorHp60Hz', type: 'number' },
        { label: 'Motor voltage @60 Hz (volts)', name: 'MotorVolts60Hz', type: 'number' },
        { label: 'VSD nameplate current (amps)', name: 'VsdNameplateAmps', type: 'number' },
        { label: 'VSD nameplate power (KVA)', name: 'VsdNameplateKva', type: 'number' },
        {
          label: 'OHTC (BTU/hrft2F)',
          name: 'OHTCDefault',
          type: 'number',
          placeholderOptionLabel: '5 - default OHTC value',
        },
        { label: 'Motor to pump speed ratio', name: 'MotorToPumpSpeedRatio', type: 'number', required: true },
        { label: 'Pump intake I/D (in)', name: 'GeometryPumpIntakeRestriction', type: 'number', required: true },
        { label: 'Shaft limit (hp)', name: 'ShaftLimitHp60Hz', type: 'number' },
        {
          label: 'Cable no.',
          name: 'CableNo',
          type: 'select',
          options: [
            { key: 1, value: 1 },
            { key: 2, value: 2 },
            { key: 3, value: 3 },
          ],
        },
        {
          label: 'Cable round/flat',
          name: 'CableRoundFlat',
          type: 'select',
          options: [
            { key: 'round', value: 'Round' },
            { key: 'flat', value: 'Flat' },
          ],
        },
        // Add a placeholder field to ensure Cable Round/Flat pairs with something
        { label: '', name: 'placeholder', type: 'hidden' },
      ],
    };

    const sections = [
      { title: 'Well depths', fields: fieldConfigs.wellDepths },
      { title: 'Well geometry', fields: fieldConfigs.wellGeometry },
      { title: 'Well equipment', fields: fieldConfigs.wellEquipment },
    ];

    const renderFormField = (field: any) => {
      // Don't render hidden placeholder fields
      if (field.type === 'hidden') {
        return <div key={field.name} style={{ flex: 1 }} />;
      }

      return (
        <FormField
          key={field.name}
          label={field.label}
          name={field.name}
          type={field.type}
          required={field?.required || false}
          value={completionsInternalValues[field.name as keyof typeof completion]}
          onChange={(_, value) => handleFieldChange(field.name, value)}
          placeholderOptionLabel={field.placeholderOptionLabel}
          options={field.options}
          error={displayErrors[field.name as keyof typeof completion]}
        />
      );
    };

    const renderFormFields = (config: any, index: number) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          {index !== 0 && <div style={{ marginTop: '16px' }} />}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid var(--border-color)',
              marginBottom: '16px',
            }}
          >
            <h3 style={{ margin: 0 }}>{config.title}</h3>
          </div>
          {config.fields.map((field: any, fieldIndex: number) => {
            if (fieldIndex % 2 === 0) {
              const nextField = config.fields[fieldIndex + 1];
              return (
                <div key={`row-${fieldIndex}`} style={{ display: 'flex', gap: 16 }}>
                  {renderFormField(field)}
                  {nextField && renderFormField(nextField)}
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    );

    return (
      <div>
        {sections.map((config, index) => (
          <div key={`${config.title}-${index}`}>{renderFormFields(config, index)}</div>
        ))}
      </div>
    );
  },
);

CompletionsCompletionTab.displayName = 'CompletionsCompletionTab';

export default CompletionsCompletionTab;
